// import dependencies
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';

import BillingAddress from '~/views/Billing/components/BillingAddress';
import InputField from '~/common/components/InputField';
import OffshoreRates from '~/views/Billing/components/OffshoreRates';
import Subheader from '~/common/components/Subheader';
import { gtm, beginCheckoutFlowEvent } from '~/common/utils/tracking';

// Import images
import lock from '~/common/images/lock.png';
import mcAfee from '~/common/images/mcAfee.png';
import verisign2 from '~/common/images/verisign2.png';
import cc from '~/common/images/cc.png';

// import stylesheet
import styles from './billingInfo.css';

@inject(
    'flexFrameStore',
    'formsStore',
    'billingInfoStore'
)
@observer
export default class BillingInfo extends Component {
    state = {
        checkoutFlowBillingInfoInit: false,
        ignoreBeginCheckoutGtmCalled: false
    };

    // validate Phone number
    validatePhoneNumber(e) {
        const { formsStore, billingInfoStore } = this.props;
        formsStore.ContactPhoneValidation(e);

        // check if the number is valid
        if (!(formsStore.doesFormHaveErrors(['ContactPhone']))) {
            billingInfoStore.isNumberOffshore(formsStore.ContactPhone);
        }
    }

    focusCCField() {
        const { flexFrameStore } = this.props;
        if (flexFrameStore.cardField) {
            flexFrameStore.cardField.focus();
            if (!this.state.checkoutFlowBillingInfoInit) {
                this.setState({ checkoutFlowBillingInfoInit: true });
                gtm(beginCheckoutFlowEvent(4));
            }
        }
    }

    focusCVCField() {
        const { flexFrameStore } = this.props;
        if (flexFrameStore.codeField) {
            flexFrameStore.codeField.focus();
            if (!this.state.checkoutFlowBillingInfoInit) {
                this.setState({ checkoutFlowBillingInfoInit: true });
                gtm(beginCheckoutFlowEvent(4));
            }
        }
    }

    // Checks if the first interaction of the form is made in this section.
    // Calls BeginCheckoutFlow event with step value. If user interacts 'Card Name' input, it would be step 3 and `Card Number` is step 4.
    callBuyFormEvent(field) {
        const { formsStore } = this.props;
        if (!formsStore.isFormInteracted()) {
            formsStore.setFormInteracted(true);
        }

        if (field && !this.state.ignoreBeginCheckoutGtmCalled) {
            this.setState({ ignoreBeginCheckoutGtmCalled: true });
            gtm(beginCheckoutFlowEvent(3));
        }

        if (!field && !this.state.checkoutFlowBillingInfoInit) {
            this.setState({ checkoutFlowBillingInfoInit: true });
            gtm(beginCheckoutFlowEvent(4));
        }
    }

    render() {
        const {
            NameOnCard,
            NameOnCardError,
            ContactPhone,
            ContactPhoneError,
            ExpirationDate,
            ExpirationDateError
        } = this.props.formsStore;

        const { offshoreWarning } = this.props.billingInfoStore;
        const {
            cardHasValue,
            codeHasValue,
            codeLabel,
            displayCardErrorUI,
            displayCodeErrorUI,
            isCardFieldFocused,
            isCodeFieldFocused
        } = this.props.flexFrameStore;

        const { isCheckoutABTest } = this.props;


        return (
            <section className={`${styles['billing-info']} ${isCheckoutABTest ? 'billing-info-test' : ''}`} ref="billingInfo">
                <header className={styles.header}>
                    <div className={styles['lock-badge']}>
                        <img
                            alt="lock image"
                            className={styles['resp-img']}
                            src={lock}
                        />
                    </div>
                    <div>
                        <Subheader modifier={styles['primary-header']}>{isCheckoutABTest ? 'Payment Details' : 'Secure Credit Card Payment'}</Subheader>
                        <h4 className={styles['secondary-header']}>This is a secure 128-bit SSL encrypted payment.</h4>
                    </div>
                    <div className={styles['badge-container']}>
                        <a
                            className={styles.mcafee}
                            href="https://www.mcafeesecure.com/RatingVerify?ref=secure.grasshopper.com"
                            tabIndex="-1"
                            target="_blank"
                        >
                            <img
                                alt="mcAfee badge"
                                className={styles['resp-img']}
                                src={mcAfee}
                            />
                        </a>
                        <img
                            alt="Verisign badge"
                            className={styles.verisign}
                            src={verisign2}
                            title="Verisign"
                        />
                    </div>
                </header>
                {isCheckoutABTest ?
                    <Fragment>
                        <InputField
                            allowedCharacters=" -'"
                            autoComplete="cc-name"
                            dataTest="name-on-cc-input"
                            defaultValue={NameOnCard}
                            error={NameOnCardError}
                            id="name-on-card"
                            label="Name on Card"
                            maxLength={50}
                            name="NameOnCard"
                            onFocus={() => this.callBuyFormEvent()}
                            restrictCharacters="alphaNumericOnly"
                            wrapperClass={styles.input}
                        />
                        <div
                            className={`${styles.input} ${styles.cc}`}
                        >
                            <div className={styles['cc-container']}>
                                <div
                                    className={`${styles['cc-number']} ${
                                        isCardFieldFocused ? styles['cc-number--focus'] : ''} ${
                                        cardHasValue ? styles['cc-number--has-value'] : ''} ${
                                        displayCardErrorUI ? styles['cc-number--error'] : ''}`}
                                    data-test="cc-flexframe-container"
                                    id="cc__number"
                                    onClick={() => { this.focusCCField(); }}
                                ></div>
                                <label
                                    className={`${styles['cc-label']} ${
                                        isCardFieldFocused ? styles['cc-label--focus'] : ''} ${
                                        cardHasValue ? styles['cc-label--has-value'] : ''} ${
                                        displayCardErrorUI ? styles['cc-label--error'] : ''}`}
                                    id="cc__label"
                                    onClick={() => { this.focusCCField(); }}
                                >Card Number</label>
                            </div>
                            {displayCardErrorUI && <div className={styles['cc-error-message']} data-test="cc-error-message">Please enter a valid credit card number.</div>}
                            {isCardFieldFocused &&
                        <img
                            alt="Credit Cards Badge"
                            className={styles['cc-img']}
                            src={cc}
                            title="ccbadge"
                        />}
                        </div>
                        <InputField
                            allowedCharacters="/"
                            dataTest="exp-date-input"
                            defaultValue={ExpirationDate}
                            error={ExpirationDateError}
                            hint="MM/YY"
                            id="exp-date"
                            inputMode="numeric"
                            label="Expiration"
                            mask={[/[0-1]/, /\d/, '/', /\d/, /\d/]}
                            name="ExpirationDate"
                            onFocus={() => this.callBuyFormEvent()}
                            pattern="[0-9]*"
                            restrictCharacters="numericOnly"
                            type="tel"
                        />
                        <div
                            className={styles.cvc}
                        >
                            <div className={styles['cvc-container']}>
                                <div
                                    className={`${styles['cvc-number']} ${
                                        isCodeFieldFocused ? styles['cvc-number--focus'] : ''} ${
                                        codeHasValue ? styles['cvc-number--has-value'] : ''} ${
                                        displayCodeErrorUI ? styles['cvc-number--error'] : ''}`}
                                    data-test="cvc-flexframe-container"
                                    id="cvc__number"
                                    onClick={() => { this.focusCVCField(); }}
                                ></div>
                                <label
                                    className={`${styles['cvc-label']} ${
                                        isCodeFieldFocused ? styles['cvc-label--focus'] : ''} ${
                                        codeHasValue ? styles['cvc-label--has-value'] : ''} ${
                                        displayCodeErrorUI ? styles['cvc-label--error'] : ''}`}
                                    id="cvc__label"
                                    onClick={() => { this.focusCVCField(); }}
                                >{codeLabel}</label>
                            </div>
                            {displayCodeErrorUI && <div className={styles['cvc-error-message']} data-test="cvc-error-message">Please enter a valid security code.</div>}
                        </div>

                    </Fragment>
                    : (
                        <Fragment>
                            <InputField
                                allowedCharacters=" -'"
                                autoComplete="cc-name"
                                dataTest="name-on-cc-input"
                                defaultValue={NameOnCard}
                                error={NameOnCardError}
                                id="name-on-card"
                                label="Name on Credit Card"
                                maxLength={50}
                                name="NameOnCard"
                                onFocus={() => this.callBuyFormEvent('name')}
                                restrictCharacters="alphaNumericOnly"
                                wrapperClass={styles.input}
                            />
                            <InputField
                                autoComplete="phone"
                                dataTest="contact-number-input"
                                defaultValue={ContactPhone}
                                error={ContactPhoneError}
                                hint="This is where your Grasshopper calls will forward to. For most people, this is their cell phone number."
                                id="contact-number"
                                label="Phone Number"
                                mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                                name="ContactPhone"
                                onFocus={() => this.callBuyFormEvent('contact')}
                                restrictCharacters="numericOnly"
                                type="tel"
                                warning={offshoreWarning}
                                wrapperClass={styles.input}
                            />
                            {offshoreWarning && <OffshoreRates />}
                            <BillingAddress ignoreBeginCheckoutGtmCalled={this.state.ignoreBeginCheckoutGtmCalled} />
                            <div
                                className={`${styles.input} ${styles.cc}`}
                            >
                                <div className={styles['cc-container']}>
                                    <div
                                        className={`${styles['cc-number']} ${
                                            isCardFieldFocused ? styles['cc-number--focus'] : ''} ${
                                            cardHasValue ? styles['cc-number--has-value'] : ''} ${
                                            displayCardErrorUI ? styles['cc-number--error'] : ''}`}
                                        data-test="cc-flexframe-container"
                                        id="cc__number"
                                        onClick={() => { this.focusCCField(); }}
                                    ></div>
                                    <label
                                        className={`${styles['cc-label']} ${
                                            isCardFieldFocused ? styles['cc-label--focus'] : ''} ${
                                            cardHasValue ? styles['cc-label--has-value'] : ''} ${
                                            displayCardErrorUI ? styles['cc-label--error'] : ''}`}
                                        id="cc__label"
                                        onClick={() => { this.focusCCField(); }}
                                    >Card Number</label>
                                </div>
                                {displayCardErrorUI && <div className={styles['cc-error-message']} data-test="cc-error-message">Please enter a valid credit card number.</div>}
                                {isCardFieldFocused &&
                                <img
                                    alt="Credit Cards Badge"
                                    className={styles['cc-img']}
                                    src={cc}
                                    title="ccbadge"
                                />}
                            </div>
                            <InputField
                                allowedCharacters="/"
                                dataTest="exp-date-input"
                                defaultValue={ExpirationDate}
                                error={ExpirationDateError}
                                hint="MM/YY"
                                id="exp-date"
                                inputMode="numeric"
                                label="Exp. Date"
                                mask={[/[0-1]/, /\d/, '/', /\d/, /\d/]}
                                name="ExpirationDate"
                                onFocus={() => this.callBuyFormEvent()}
                                pattern="[0-9]*"
                                restrictCharacters="numericOnly"
                                type="tel"
                            />
                            <div
                                className={styles.cvc}
                            >
                                <div className={styles['cvc-container']}>
                                    <div
                                        className={`${styles['cvc-number']} ${
                                            isCodeFieldFocused ? styles['cvc-number--focus'] : ''} ${
                                            codeHasValue ? styles['cvc-number--has-value'] : ''} ${
                                            displayCodeErrorUI ? styles['cvc-number--error'] : ''}`}
                                        data-test="cvc-flexframe-container"
                                        id="cvc__number"
                                        onClick={() => { this.focusCVCField(); }}
                                    ></div>
                                    <label
                                        className={`${styles['cvc-label']} ${
                                            isCodeFieldFocused ? styles['cvc-label--focus'] : ''} ${
                                            codeHasValue ? styles['cvc-label--has-value'] : ''} ${
                                            displayCodeErrorUI ? styles['cvc-label--error'] : ''}`}
                                        id="cvc__label"
                                        onClick={() => { this.focusCVCField(); }}
                                    >{codeLabel}</label>
                                </div>
                                {displayCodeErrorUI && <div className={styles['cvc-error-message']} data-test="cvc-error-message">Please enter a valid security code.</div>}
                            </div>

                        </Fragment>
                    )
                }
            </section>
        );
    }
}
